/** ตรรกะออกบัญชี/รีเซ็ตรหัสของพนักงานหนึ่งคน — ใช้ร่วมระหว่างแผงในการ์ดและ dialog ในแถวตาราง */

import { useState } from 'react';

import { USERNAME_PATTERN } from './employeeLoginView';
import {
  issueEmployeeCredential,
  resetEmployeePassword,
  type IssueLoginResult,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import type { Employee, IssuedLogin } from '../../lib/rbacAdmin/types';

export interface UseEmployeeLoginOptions {
  readonly employee: Employee;
  readonly canEdit: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly onEmployeeChanged: () => void;
  readonly initialUsername?: string;
}

/** ข้อเสนอ username จากรหัสพนักงาน — ว่างถ้าไม่ตรงรูปแบบ */
export function suggestUsername(employeeId: string): string {
  const candidate = employeeId.toLowerCase();
  return USERNAME_PATTERN.test(candidate) ? candidate : '';
}

export function useEmployeeLogin({
  employee,
  canEdit,
  apiBaseUrl,
  fetchImpl,
  onEmployeeChanged,
  initialUsername = '',
}: UseEmployeeLoginOptions) {
  const [username, setUsername] = useState(initialUsername);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedLogin | null>(null);
  const [failure, setFailure] = useState<RbacAdminFailure | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const usernameValid = USERNAME_PATTERN.test(username);

  const receive = (result: IssueLoginResult) => {
    if (result.status === 'ready') {
      setIssued(result.issued);
      setFailure(null);
      setCopyState('idle');
      setUsername('');
      onEmployeeChanged();
    } else {
      setFailure(result);
    }
  };

  const issue = async () => {
    if (!canEdit || busy || !usernameValid) {
      return;
    }
    setBusy(true);
    receive(await issueEmployeeCredential(employee.employee_id, username, { apiBaseUrl, fetchImpl }));
    setBusy(false);
  };

  const reset = async () => {
    if (!canEdit || busy) {
      return;
    }
    setBusy(true);
    const result = await resetEmployeePassword(employee.employee_id, { apiBaseUrl, fetchImpl });
    receive(result);
    setBusy(false);
  };

  const copy = async () => {
    if (issued === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.temporary_password);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const dismissIssued = () => {
    setIssued(null);
    setCopyState('idle');
  };

  return {
    username,
    setUsername,
    usernameValid,
    busy,
    issued,
    failure,
    setFailure,
    copyState,
    issue,
    reset,
    copy,
    dismissIssued,
  };
}
